import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { AlertTriangle, Clock, HelpCircle, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
export const PerformanceReliabilitySection: React.FC<{ data: any }> = ({
  data,
}) => {
  const navigate = useNavigate();
  const reliabilityMetrics = [
    {
      title: "Portfolio Failure Rate",
      count: `${
        data?.performanceReliability?.portfolioFailureRate?.value ?? "NA"
      } ${data?.performanceReliability?.portfolioFailureRate?.unit ?? "NA"}`,
      trend: data?.performanceReliability?.portfolioFailureRate?.trend,
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
      tooltip:
        "The percentage of assets that have had at least one failure in the selected period.",
    },
    {
      title: "Avg MTBF",
      count: `${data?.performanceReliability?.avgMtbf?.value ?? "NA"} ${
        data?.performanceReliability?.avgMtbf?.unit ?? "NA"
      }`,
      trend: data?.performanceReliability?.avgMtbf?.trend,
      icon: <Clock className="h-8 w-8 text-blue-500" />,
      tooltip:
        "The average operational time between failures for assets across the portfolio. A higher number is better.",
    },
    {
      title: "Service Level Achievement",
      count: `${
        data?.performanceReliability?.serviceLevelAchievement?.value ?? "NA"
      } ${data?.performanceReliability?.serviceLevelAchievement?.unit ?? "NA"}`,
      trend: data?.performanceReliability?.serviceLevelAchievement?.trend,
      icon: <Target className="h-8 w-8 text-green-500" />,
      tooltip:
        "The percentage of time that operational service levels (e.g., pressure, flow) were met.",
    },
    // {
    //   title: "Non-Revenue Water",
    //   count: `${data?.performanceReliability?.nonRevenueWater?.value} ${data?.performanceReliability?.nonRevenueWater?.unit}`,
    //   trend: data?.performanceReliability?.nonRevenueWater?.trend,
    //   icon: <Droplets className="h-8 w-8 text-orange-500" />,
    //   tooltip:
    //     "The percentage of treated water that is lost or unaccounted for before it reaches the customer. A lower number is better.",
    // },
  ];

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case "critical":
        return "border-red-200 bg-red-50 text-red-900";
      case "moderate":
        return "border-yellow-200 bg-yellow-50 text-yellow-900";
      default:
        return "border-gray-200 bg-gray-50 text-gray-900";
    }
  };

  const getSeverityTextStyles = (severity: string) => {
    switch (severity) {
      case "critical":
        return { primary: "text-red-700", secondary: "text-red-600" };
      case "moderate":
        return { primary: "text-yellow-700", secondary: "text-yellow-600" };
      default:
        return { primary: "text-gray-700", secondary: "text-gray-600" };
    }
  };

  return (
    <div className="space-y-6">
      <p className="page-title">Performance & Reliability</p>

      {/* Key Performance Metrics using QuickStatsCard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {reliabilityMetrics.map((metric, index) => (
          <QuickStatsCard
            key={index}
            title={metric.title}
            count={metric.count}
            trend={metric.trend}
            icon={metric.icon}
            tooltip={metric.tooltip}
          />
        ))}
      </div>

      {/* Frequent Failure Assets */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div>
              <CardTitle className="page-title">
                Frequent Failure Assets
              </CardTitle>
            </div>
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    This list displays the top 3 assets with the highest number
                    of failure-related work orders in the last 6 months.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <CardDescription className="page-subtitle">
            Assets requiring immediate attention
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data?.frequentFailureAssets?.map((asset, index) => {
              const severityStyles = getSeverityStyles(asset.riskLevel);
              const textStyles = getSeverityTextStyles(asset.riskLevel);

              return (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${severityStyles}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{asset.assetName}</p>
                      <p className={`text-sm ${textStyles.primary}`}>
                        {asset.failureCount}
                      </p>
                      <p className={`text-xs ${textStyles.secondary}`}>
                        Last failure: {asset.lastFailureDate}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/ax/asset-registry/assets/view/${asset.assetId}`
                        )
                      }
                    >
                      View
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
